import { useCallback, useMemo, useState } from "react";
import { OnChangeFn, SortingState } from "@/components/Datatable/types";
import { downloadFile } from "@/utils/downloadFile";
import { useMonthlyReportData } from "../../shared/useMonthlyReportData";
import { MonthlyReportRow } from "../api/fetchMonthlyReport";
import {
  MONTHLY_REPORT_FIELDS,
  NUMERIC_MONTHLY_REPORT_FIELDS,
} from "../components/monthlyReportColumns";

const DEFAULT_PAGE_SIZE = 20;

// UTF-8 BOM so Excel opens the exported CSV with the correct encoding.
const UTF8_BOM = String.fromCharCode(0xfeff);

// Leading characters a spreadsheet may interpret as the start of a formula.
// Besides the obvious `=`/`+`/`-`/`@`, Excel and Google Sheets also honor a
// leading TAB (0x09) or CR (0x0D) as a formula start, so those are neutralized
// too.
const CSV_FORMULA_TRIGGERS = ["=", "+", "-", "@", "\t", "\r"];

/**
 * Escapes a value for CSV output.
 *
 * Two concerns are handled, in order:
 *  1. CSV formula injection — a value whose first character is `=`, `+`, `-`,
 *     or `@` could execute as a formula in Excel/Sheets. We neutralize it by
 *     prefixing a single quote so the cell is treated as literal text. Valid
 *     numbers (e.g. a negative metric like `-51`) are exempt so they still
 *     export as numbers rather than being mangled into text.
 *  2. RFC-4180 quoting — values containing quotes, commas, or newlines are
 *     wrapped in double quotes with embedded quotes doubled.
 */
export const escapeCsvValue = (value: string | number): string => {
  let stringValue = value == null ? "" : String(value);
  if (
    stringValue.length > 0 &&
    CSV_FORMULA_TRIGGERS.includes(stringValue[0]) &&
    Number.isNaN(Number(stringValue))
  ) {
    stringValue = `'${stringValue}`;
  }
  return /[",\n\r]/.test(stringValue)
    ? `"${stringValue.replace(/"/g, '""')}"`
    : stringValue;
};

// Numeric sort must never produce NaN: a non-numeric/blank cell would poison
// the comparison and scramble the whole column's order. Coerce anything
// non-finite to 0 (matching how the leaderboards coerce metric values) so the
// sort stays stable and predictable.
const toSortableNumber = (value: string | number): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const useMonthlyReport = () => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Fresh data invalidates the view state built for the previous data set.
  const resetView = useCallback(() => {
    setPageIndex(0);
    setSorting([]);
  }, []);

  const { failed, hasRun, loading, meta, rows, run } =
    useMonthlyReportData(resetView);

  const sortedRows = useMemo(() => {
    if (sorting.length === 0) {
      return rows;
    }
    const { id, desc } = sorting[0];
    const key = id as keyof MonthlyReportRow;
    const isNumeric = NUMERIC_MONTHLY_REPORT_FIELDS.has(key);

    return [...rows].sort((a, b) => {
      const aValue = a[key];
      const bValue = b[key];
      const comparison = isNumeric
        ? toSortableNumber(aValue) - toSortableNumber(bValue)
        : String(aValue ?? "").localeCompare(String(bValue ?? ""));
      return desc ? -comparison : comparison;
    });
  }, [rows, sorting]);

  const pageRows = useMemo(() => {
    const start = pageIndex * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, pageIndex, pageSize]);

  const handlePageChange = useCallback(
    (newPageIndex: number, newPageSize: number) => {
      setPageIndex(newPageIndex);
      setPageSize(newPageSize);
    },
    [],
  );

  const handleSortingChange: OnChangeFn<SortingState> = useCallback(
    (updaterOrValue) => {
      setSorting((prev) =>
        typeof updaterOrValue === "function"
          ? updaterOrValue(prev)
          : updaterOrValue,
      );
      setPageIndex(0);
    },
    [],
  );

  const exportCsv = useCallback(() => {
    if (sortedRows.length === 0) {
      return;
    }
    const headerLine = MONTHLY_REPORT_FIELDS.map((field) =>
      escapeCsvValue(field.header),
    ).join(",");
    const dataLines = sortedRows.map((row) =>
      MONTHLY_REPORT_FIELDS.map((field) => escapeCsvValue(row[field.key])).join(
        ",",
      ),
    );
    const csv = [headerLine, ...dataLines].join("\r\n");

    const blob = new Blob([`${UTF8_BOM}${csv}`], {
      type: "text/csv;charset=utf-8;",
    });
    downloadFile(
      blob,
      `scoreboard-monthly-report-${meta?.timePeriod ?? "export"}.csv`,
    );
  }, [sortedRows, meta]);

  return {
    exportCsv,
    failed,
    handlePageChange,
    handleSortingChange,
    hasRun,
    loading,
    meta,
    pageIndex,
    pageRows,
    pageSize,
    sorting,
    totalCount: sortedRows.length,
    run,
  };
};
